import React, { useEffect, useState, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Layout from "./components/Layout";
import SignIn from "./components/SignIn";
import StudioPackages from "./components/StudioPackages";

const isTokenValid = () => {
  const token = localStorage.getItem("manager_token");
  const expiresAt = localStorage.getItem("manager_expiresAt");

  if (!token || !expiresAt) return false;

  const expiryTime = new Date(expiresAt).getTime();

  if (isNaN(expiryTime)) return false;

  return Date.now() < expiryTime;
};

function ProtectedRoute({ isAuthenticated }) {
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function ManagerLayout({ username, onLogout }) {
  return (
    <Layout username={username} onLogout={onLogout}>
      <Outlet />
    </Layout>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState(isTokenValid());
  const [username, setUsername] = useState(
    localStorage.getItem("manager_username") || ""
  );

  const handleSignIn = useCallback(
    (user) => {
      setIsAuthenticated(true);
      setUsername(user);

      navigate("/Home", { replace: true });
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem("manager_token");
    localStorage.removeItem("manager_expiresAt");
    localStorage.removeItem("manager_username");
    sessionStorage.removeItem("manager_activeTab");

    setIsAuthenticated(false);
    setUsername("");

    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const valid = isTokenValid();

    if (!valid) {
      setIsAuthenticated(false);
      setUsername("");
      localStorage.removeItem("manager_token");
      localStorage.removeItem("manager_expiresAt");
      localStorage.removeItem("manager_username");
      sessionStorage.removeItem("manager_activeTab");
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      if (!isTokenValid()) {
        handleLogout();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, handleLogout]);

  useEffect(() => {
    if (isAuthenticated && location.pathname === "/") {
      navigate("/Home", { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/" element={<SignIn onSignIn={handleSignIn} />} />

      <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
        <Route
          path="/Home"
          element={
            <ManagerLayout
              username={username}
              onLogout={handleLogout}
            />
          }
        >
          <Route index element={<StudioPackages />} />
        </Route>
      </Route>

      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? "/Home" : "/"} replace />}
      />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}